// Utility functions for analytics data formatting

export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  category?: string;
  category2?: string;
  category3?: string;
  item_brand?: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

// Format Shopify product for GA4 events
export const formatProductForAnalytics = (
  product: any,
  quantity: number = 1
): AnalyticsItem => {
  return {
    item_id: product.id || product.handle,
    item_name: product.title,
    category: product.productType || 'Spices',
    item_brand: 'Olispices',
    item_variant:
      product.selectedVariant?.title ||
      product.variants?.edges?.[0]?.node?.title,
    price: parseFloat(
      product.selectedVariant?.price ||
        product.variants?.edges?.[0]?.node?.price ||
        '0'
    ),
    quantity,
  };
};

// Format recipe for analytics
export const formatRecipeForAnalytics = (recipe: any): AnalyticsItem => {
  return {
    item_id: recipe.id || recipe.slug,
    item_name: recipe.title,
    category: recipe.category || 'Recipe',
    item_brand: 'Olispices',
    price: 0, // Recipes are free
    quantity: 1,
  };
};

// Calculate cart value
export const calculateCartValue = (items: AnalyticsItem[]): number => {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
};

// Get currency based on market context
export const getCurrency = (market?: string): string => {
  const currencyMap: { [key: string]: string } = {
    US: 'USD',
    DE: 'EUR',
    PL: 'PLN',
    UA: 'UAH',
    default: 'USD',
  };

  return currencyMap[market || 'default'] || currencyMap['default'];
};

// Enhanced user properties
export const setUserProperties = (properties: { [key: string]: any }) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA4_MEASUREMENT_ID, {
      user_properties: properties,
    });
  }
};

// Set user ID for cross-platform tracking
export const setUserId = (userId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA4_MEASUREMENT_ID, {
      user_id: userId,
    });
  }
};

// Performance tracking
export const trackPerformance = (metricName: string, value: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'timing_complete', {
      name: metricName,
      value: Math.round(value),
    });
  }
};

// Error tracking
export const trackError = (error: Error, errorInfo?: any) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'exception', {
      description: error.message,
      fatal: false,
      error_stack: error.stack,
      error_info: JSON.stringify(errorInfo),
    });
  }
};

// Consent management
export const setConsent = (consentSettings: {
  ad_storage?: 'granted' | 'denied';
  analytics_storage?: 'granted' | 'denied';
  functionality_storage?: 'granted' | 'denied';
  personalization_storage?: 'granted' | 'denied';
  security_storage?: 'granted' | 'denied';
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', consentSettings);
  }
};

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}
